import React, { useEffect, useMemo, useState, useCallback } from 'react';
import Select from 'react-select';
import { BsPlusCircle, BsXCircle, BsHeadset, BsArrowRepeat } from 'react-icons/bs';
import { useNavigate } from 'react-router-dom';
import { apiFetch, parseJsonSafe, formatDate } from '../utils/api';

const STATUS_OPTIONS = [
  { value: 'abierto', label: 'Abierto' },
  { value: 'en_proceso', label: 'En proceso' },
  { value: 'esperando_cliente', label: 'Esperando cliente' },
  { value: 'resuelto', label: 'Resuelto' },
  { value: 'cerrado', label: 'Cerrado' }
];
const PRIORITY_OPTIONS = [
  { value: 'baja', label: 'Baja' },
  { value: 'normal', label: 'Normal' },
  { value: 'alta', label: 'Alta' },
  { value: 'urgente', label: 'Urgente' }
];
const CATEGORY_OPTIONS = [
  { value: 'general', label: 'General' },
  { value: 'incidencia', label: 'Incidencia' },
  { value: 'consulta', label: 'Consulta' },
  { value: 'facturacion', label: 'Facturación' },
  { value: 'otro', label: 'Otro' }
];

const STATUS_BADGE = { abierto: 'primary', en_proceso: 'warning text-dark', esperando_cliente: 'secondary', resuelto: 'success', cerrado: 'dark' };
const PRIORITY_BADGE = { baja: 'secondary', normal: 'info text-dark', alta: 'warning text-dark', urgente: 'danger' };
const labelOf = (opts, v) => (opts.find((o) => o.value === v)?.label || v || '-');

export const StatusPill = ({ status }) => (
  <span className={`badge bg-${STATUS_BADGE[status] || 'secondary'}`}>{labelOf(STATUS_OPTIONS, status)}</span>
);
export const PriorityPill = ({ priority }) => (
  <span className={`badge bg-${PRIORITY_BADGE[priority] || 'secondary'}`}>{labelOf(PRIORITY_OPTIONS, priority)}</span>
);

const SupportTicketsPage = () => {
  const token = localStorage.getItem('token');
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [onlyUnread, setOnlyUnread] = useState(false);
  const [q, setQ] = useState('');
  // Respuesta automática de la IA a los tickets del portal (acuse de recibo inmediato)
  const [aiAutoReply, setAiAutoReply] = useState(null);
  const isAdmin = localStorage.getItem('role') === 'admin';
  useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch('/api/support/settings/ai-auto-reply', { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
        const data = await parseJsonSafe(res);
        if (res.ok) setAiAutoReply(Boolean(data.enabled));
      } catch (_e) { /* silencioso */ }
    })();
  }, []);
  const toggleAiAutoReply = async (enabled) => {
    setAiAutoReply(enabled);
    try {
      const res = await apiFetch('/api/support/settings/ai-auto-reply', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ enabled })
      });
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.message || 'No se pudo guardar');
      setAiAutoReply(Boolean(data.enabled));
    } catch (err) {
      setAiAutoReply(!enabled);
      setError(err.message);
    }
  };
  const [modalOpen, setModalOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ client_id: null, subject: '', priority: 'normal', category: 'general', body: '' });
  const [formError, setFormError] = useState('');

  const handleUnauthorized = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('userEmail');
    window.location.href = '/login';
  };

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const qs = new URLSearchParams();
      if (statusFilter) qs.append('status', statusFilter);
      if (priorityFilter) qs.append('priority', priorityFilter);
      if (onlyUnread) qs.append('unread', 'true');
      if (q.trim()) qs.append('q', q.trim());
      qs.append('pageSize', '100');
      const res = await apiFetch(`/api/support?${qs.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.status === 401) return handleUnauthorized();
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.message || 'Error cargando tickets');
      setTickets(Array.isArray(data.items) ? data.items : []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, priorityFilter, onlyUnread, q, token]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const openModal = async () => {
    setForm({ client_id: null, subject: '', priority: 'normal', category: 'general', body: '' });
    setFormError('');
    setModalOpen(true);
    if (!clients.length) {
      try {
        const res = await apiFetch('/api/clients?page=1&pageSize=500', { headers: { Authorization: `Bearer ${token}` } });
        const data = await parseJsonSafe(res);
        if (res.ok) setClients(data.data || data.items || []);
      } catch (_e) { /* sin clientes */ }
    }
  };

  const clientOptions = useMemo(
    () => clients.map((c) => ({ value: c.id, label: c.razon_social || c.email || `Cliente #${c.id}` })),
    [clients]
  );

  const saveTicket = async () => {
    if (!form.client_id) { setFormError('Selecciona un cliente'); return; }
    if (!form.subject.trim()) { setFormError('El asunto es obligatorio'); return; }
    setSaving(true);
    setFormError('');
    try {
      const res = await apiFetch('/api/support', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form)
      });
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.message || 'No se pudo crear el ticket');
      setModalOpen(false);
      navigate(`/soporte/${data.id}`);
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
        <h2 className="mb-0 d-flex align-items-center"><BsHeadset className="me-2" /> Soporte</h2>
        <div className="d-flex gap-2 align-items-center flex-wrap">
          {aiAutoReply !== null && (
            <div className="form-check form-switch mb-0 me-2" title="La IA contesta al momento a los tickets nuevos del portal con un acuse de recibo breve (sin comprometer información). El equipo sigue viendo el ticket como pendiente.">
              <input className="form-check-input" type="checkbox" id="aiAutoReply" checked={aiAutoReply} disabled={!isAdmin} onChange={(e) => toggleAiAutoReply(e.target.checked)} />
              <label className="form-check-label small" htmlFor="aiAutoReply">Acuse de recibo automático (IA)</label>
            </div>
          )}
          <button className="btn btn-outline-secondary" onClick={fetchTickets} title="Actualizar"><BsArrowRepeat /></button>
          <button className="btn btn-primary d-flex align-items-center" onClick={openModal}>
            <BsPlusCircle className="me-2" /> Nuevo ticket
          </button>
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-body d-flex flex-wrap gap-2 align-items-end">
          <div>
            <label className="form-label small mb-1">Estado</label>
            <select className="form-select form-select-sm" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">Todos</option>
              {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="form-label small mb-1">Prioridad</label>
            <select className="form-select form-select-sm" value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)}>
              <option value="">Todas</option>
              {PRIORITY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div className="flex-grow-1" style={{ minWidth: 200 }}>
            <label className="form-label small mb-1">Buscar</label>
            <input className="form-control form-control-sm" placeholder="Asunto o cliente…" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <div className="form-check mb-1">
            <input className="form-check-input" type="checkbox" id="onlyUnread" checked={onlyUnread} onChange={(e) => setOnlyUnread(e.target.checked)} />
            <label className="form-check-label small" htmlFor="onlyUnread">Solo sin leer</label>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="card">
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead>
              <tr>
                <th>#</th>
                <th>Cliente</th>
                <th>Asunto</th>
                <th>Estado</th>
                <th>Prioridad</th>
                <th>Asignado</th>
                <th>Última actividad</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="7" className="text-center text-muted py-4">Cargando…</td></tr>
              ) : !tickets.length ? (
                <tr><td colSpan="7" className="text-center text-muted py-4">No hay tickets.</td></tr>
              ) : tickets.map((t) => (
                <tr key={t.id} style={{ cursor: 'pointer', fontWeight: t.staff_unread ? 600 : 400 }} onClick={() => navigate(`/soporte/${t.id}`)}>
                  <td>
                    {t.staff_unread && <span className="badge bg-danger rounded-circle me-1 p-1" style={{ verticalAlign: 'middle' }}> </span>}
                    {t.id}
                  </td>
                  <td>{t.client_name || '-'}</td>
                  <td>{t.subject} <span className="text-muted small">({t.message_count})</span></td>
                  <td><StatusPill status={t.status} /></td>
                  <td><PriorityPill priority={t.priority} /></td>
                  <td className="small">{t.assigned_name || <span className="text-muted">Sin asignar</span>}</td>
                  <td className="small text-muted">{formatDate(t.last_message_at || t.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {modalOpen && (
        <div className="modal-backdrop-custom" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 1050, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '5vh 12px', overflowY: 'auto' }}>
          <div className="card" style={{ width: '100%', maxWidth: 560 }}>
            <div className="card-header d-flex justify-content-between align-items-center">
              <strong>Nuevo ticket</strong>
              <button className="btn btn-sm btn-link text-dark p-0" onClick={() => setModalOpen(false)}><BsXCircle size={20} /></button>
            </div>
            <div className="card-body">
              {formError && <div className="alert alert-danger py-2">{formError}</div>}
              <div className="mb-3">
                <label className="form-label">Cliente *</label>
                <Select
                  options={clientOptions}
                  value={clientOptions.find((o) => o.value === form.client_id) || null}
                  onChange={(opt) => setForm((p) => ({ ...p, client_id: opt ? opt.value : null }))}
                  placeholder="Selecciona un cliente…"
                  isClearable
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Asunto *</label>
                <input className="form-control" value={form.subject} onChange={(e) => setForm((p) => ({ ...p, subject: e.target.value }))} />
              </div>
              <div className="row">
                <div className="col-6 mb-3">
                  <label className="form-label">Prioridad</label>
                  <select className="form-select" value={form.priority} onChange={(e) => setForm((p) => ({ ...p, priority: e.target.value }))}>
                    {PRIORITY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div className="col-6 mb-3">
                  <label className="form-label">Categoría</label>
                  <select className="form-select" value={form.category} onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))}>
                    {CATEGORY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="mb-2">
                <label className="form-label">Descripción</label>
                <textarea className="form-control" rows="4" value={form.body} onChange={(e) => setForm((p) => ({ ...p, body: e.target.value }))} placeholder="Detalle de la incidencia o consulta…" />
              </div>
            </div>
            <div className="card-footer d-flex justify-content-end gap-2">
              <button className="btn btn-outline-secondary" onClick={() => setModalOpen(false)} disabled={saving}>Cancelar</button>
              <button className="btn btn-primary" onClick={saveTicket} disabled={saving}>{saving ? 'Creando…' : 'Crear ticket'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportTicketsPage;
